/**
 * Cost-of-breaking-rules metrics for Account Health.
 *
 * Prefers a backend-provided `rule_break_costs` object on metrics summary when present.
 * Otherwise derives what we can from `/trading_rules_breaches` + violation metadata.
 *
 * Expected optional violation metadata:
 * - trade_without_stop_loss / require_stop_loss: realized_pnl | trade_pnl | pnl | closed_pnl
 * - max_trades_per_day: after_limit_pnls (number[]) and/or after_limit_pnl_sum + after_limit_trade_count
 */

import { countsTowardViolationStats, normalizeViolationRuleType } from './violationLimitActual';

const NO_STOP_TYPES = new Set(['trade_without_stop_loss', 'require_stop_loss']);
const MAX_TRADES_TYPES = new Set(['max_trades_per_day']);

function asFiniteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function startOfLocalDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]00:00(?::00(?:\.\d+)?)?Z?)?$/;

/**
 * A breach date is a calendar day, sent as '2026-02-03' or the equivalent
 * UTC-midnight timestamp. Both parse as UTC, which is the day before for
 * anyone behind UTC, so read the calendar fields off the string instead.
 */
function startOfBreachDay(value) {
  if (typeof value === 'string') {
    const parts = CALENDAR_DAY.exec(value.trim());
    if (parts) return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  }
  return startOfLocalDay(value);
}

/**
 * Filter breach rows to the dashboard timeframe (client-side; mirrors LossLimitHistoryTimeline).
 */
export function filterBreachesByTimeRange(breaches = [], timeRange = 'month') {
  if (!Array.isArray(breaches) || breaches.length === 0) return [];
  const today = startOfLocalDay(new Date());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  let daysToInclude;
  switch (timeRange) {
    case 'today':
      daysToInclude = 1;
      break;
    case '7days':
    case 'week':
      daysToInclude = 7;
      break;
    case '30days':
    case 'month':
      daysToInclude = 30;
      break;
    case 'quarter':
      daysToInclude = 90;
      break;
    case 'ytd': {
      const yearStart = new Date(today.getFullYear(), 0, 1);
      return breaches.filter((breach) => {
        const breachDate = startOfBreachDay(breach.breach_date || breach.date);
        return breachDate >= yearStart && breachDate < tomorrow;
      });
    }
    case 'year':
      daysToInclude = 365;
      break;
    case 'all':
      return breaches.slice();
    default:
      daysToInclude = 90;
  }

  if (timeRange === 'today') {
    return breaches.filter((breach) => {
      const breachDate = startOfBreachDay(breach.breach_date || breach.date);
      return breachDate >= today && breachDate < tomorrow;
    });
  }

  const cutoffDate = new Date(today);
  cutoffDate.setDate(cutoffDate.getDate() - daysToInclude);
  return breaches.filter((breach) => {
    const breachDate = startOfBreachDay(breach.breach_date || breach.date);
    return breachDate >= cutoffDate && breachDate < tomorrow;
  });
}

function parseMeta(raw) {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  return typeof raw === 'object' ? raw : {};
}

function extractTradePnl(meta) {
  return asFiniteNumber(
    meta.realized_pnl ?? meta.trade_pnl ?? meta.pnl ?? meta.closed_pnl ?? meta.realizedPnl
  );
}

function extractAfterLimitPnls(meta) {
  if (Array.isArray(meta.after_limit_pnls)) {
    return meta.after_limit_pnls.map(asFiniteNumber).filter((n) => n !== null);
  }
  if (Array.isArray(meta.excess_trades_pnl)) {
    return meta.excess_trades_pnl.map(asFiniteNumber).filter((n) => n !== null);
  }
  const sum = asFiniteNumber(meta.after_limit_pnl_sum ?? meta.after_limit_pnl);
  const count = asFiniteNumber(meta.after_limit_trade_count);
  if (sum !== null && count !== null && count > 0) {
    // Expand to equal slices only for averaging when we lack per-trade list —
    // callers use sum/count directly; return empty list and rely on sum/count fields.
    return { __sum: sum, __count: Math.floor(count) };
  }
  if (sum !== null) {
    return { __sum: sum, __count: 1 };
  }
  return [];
}

function avg(total, count) {
  if (!count || count <= 0) return null;
  return total / count;
}

function emptyBucket() {
  return {
    total_pnl: null,
    avg_pnl: null,
    trade_count: 0,
    sample_count: 0,
    available: false,
  };
}

/**
 * Excess $ beyond the daily loss limit at EOD for unrecovered, finalized breaches.
 * Returns a negative number when the day finished worse than the limit.
 */
export function excessLossBeyondDailyLimit(breach) {
  if (!breach || breach.is_pending || breach.recovered === true) return null;
  const eod = asFiniteNumber(breach.end_of_day_loss);
  let limit = asFiniteNumber(breach.loss_limit ?? breach.limit);
  if (eod === null || limit === null) return null;
  // Limits are stored as negatives (e.g. -500). Normalize positive configs.
  if (limit > 0) limit = -Math.abs(limit);
  // Only count days that finished at or beyond the limit.
  if (eod > limit) return null;
  const excess = eod - limit;
  return excess < 0 ? excess : 0;
}

function computeNoStopCosts(violations = []) {
  const pnls = [];
  for (const v of violations) {
    const ruleType = normalizeViolationRuleType(v?.rule_type);
    if (!NO_STOP_TYPES.has(ruleType)) continue;
    if (!countsTowardViolationStats(v)) continue;
    const pnl = extractTradePnl(parseMeta(v.violation_metadata));
    if (pnl === null) continue;
    pnls.push(pnl);
  }
  if (pnls.length === 0) {
    return emptyBucket();
  }
  const total = pnls.reduce((a, b) => a + b, 0);
  return {
    total_pnl: total,
    avg_pnl: avg(total, pnls.length),
    trade_count: pnls.length,
    sample_count: pnls.length,
    available: true,
  };
}

function computeMaxTradesExcessCosts(violations = []) {
  // One contribution per calendar day (ET date from violation_timestamp / breach day).
  const byDay = new Map();

  for (const v of violations) {
    const ruleType = normalizeViolationRuleType(v?.rule_type);
    if (!MAX_TRADES_TYPES.has(ruleType)) continue;
    const meta = parseMeta(v.violation_metadata);
    const dayKey =
      meta.trade_date ||
      meta.breach_date ||
      (v.violation_timestamp ? String(v.violation_timestamp).slice(0, 10) : null);
    if (!dayKey) continue;

    const extracted = extractAfterLimitPnls(meta);
    let daySum = 0;
    let dayCount = 0;
    if (Array.isArray(extracted)) {
      if (extracted.length === 0) continue;
      daySum = extracted.reduce((a, b) => a + b, 0);
      dayCount = extracted.length;
    } else if (extracted && typeof extracted === 'object') {
      daySum = extracted.__sum;
      dayCount = extracted.__count;
    } else {
      continue;
    }

    const prev = byDay.get(dayKey);
    // Prefer the richest sample if multiple violations land on the same day.
    if (!prev || dayCount > prev.count) {
      byDay.set(dayKey, { sum: daySum, count: dayCount });
    }
  }

  if (byDay.size === 0) {
    return {
      ...emptyBucket(),
      day_count: 0,
    };
  }

  let total = 0;
  let tradeCount = 0;
  byDay.forEach(({ sum, count }) => {
    total += sum;
    tradeCount += count;
  });

  return {
    total_pnl: total,
    avg_pnl: avg(total, tradeCount),
    trade_count: tradeCount,
    sample_count: tradeCount,
    day_count: byDay.size,
    available: true,
  };
}

function computeDailyLossUnrecoveredCosts(breaches = []) {
  // Prefer one row per breach_date (keep the worst excess) so duplicate API rows don't inflate cost.
  const byDay = new Map();
  for (const breach of breaches) {
    const excess = excessLossBeyondDailyLimit(breach);
    if (excess === null) continue;
    const dayKey = breach.breach_date || breach.date;
    if (!dayKey) continue;
    const prev = byDay.get(dayKey);
    if (prev === undefined || excess < prev) {
      byDay.set(dayKey, excess);
    }
  }
  const excesses = Array.from(byDay.values());
  if (excesses.length === 0) {
    return {
      total_excess_loss: null,
      avg_excess_loss: null,
      day_count: 0,
      available: false,
    };
  }
  const total = excesses.reduce((a, b) => a + b, 0);
  return {
    total_excess_loss: total,
    avg_excess_loss: avg(total, excesses.length),
    day_count: excesses.length,
    available: true,
  };
}

function normalizePrecomputed(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const noStop = raw.no_stop_loss || raw.without_stop_loss || null;
  const daily = raw.daily_loss_unrecovered || raw.daily_loss_excess || null;
  const maxTrades = raw.max_trades_excess || raw.after_max_trades || null;

  const no_stop_loss = noStop
    ? {
        total_pnl: asFiniteNumber(noStop.total_pnl),
        avg_pnl: asFiniteNumber(noStop.avg_pnl),
        trade_count: asFiniteNumber(noStop.trade_count) ?? 0,
        sample_count: asFiniteNumber(noStop.sample_count ?? noStop.trade_count) ?? 0,
        available: true,
      }
    : emptyBucket();

  const daily_loss_unrecovered = daily
    ? {
        total_excess_loss: asFiniteNumber(daily.total_excess_loss ?? daily.total_pnl),
        avg_excess_loss: asFiniteNumber(daily.avg_excess_loss ?? daily.avg_pnl),
        day_count: asFiniteNumber(daily.day_count) ?? 0,
        available: true,
      }
    : {
        total_excess_loss: null,
        avg_excess_loss: null,
        day_count: 0,
        available: false,
      };

  const max_trades_excess = maxTrades
    ? {
        total_pnl: asFiniteNumber(maxTrades.total_pnl),
        avg_pnl: asFiniteNumber(maxTrades.avg_pnl),
        trade_count: asFiniteNumber(maxTrades.trade_count) ?? 0,
        sample_count: asFiniteNumber(maxTrades.sample_count ?? maxTrades.trade_count) ?? 0,
        day_count: asFiniteNumber(maxTrades.day_count) ?? 0,
        available: true,
      }
    : {
        ...emptyBucket(),
        day_count: 0,
      };

  return {
    no_stop_loss,
    daily_loss_unrecovered,
    max_trades_excess,
    total_impact: asFiniteNumber(raw.total_impact ?? raw.total_lost ?? raw.total_cost),
    source: 'api',
  };
}

function sumImpact(parts) {
  let total = 0;
  let any = false;
  const { no_stop_loss, daily_loss_unrecovered, max_trades_excess } = parts;
  if (no_stop_loss.available && no_stop_loss.total_pnl !== null) {
    total += no_stop_loss.total_pnl;
    any = true;
  }
  if (daily_loss_unrecovered.available && daily_loss_unrecovered.total_excess_loss !== null) {
    total += daily_loss_unrecovered.total_excess_loss;
    any = true;
  }
  if (max_trades_excess.available && max_trades_excess.total_pnl !== null) {
    total += max_trades_excess.total_pnl;
    any = true;
  }
  return any ? total : null;
}

/**
 * @param {Object} args
 * @param {Object} [args.metrics] - summary metrics (may include rule_break_costs)
 * @param {Array} [args.breaches] - daily loss breaches
 * @param {Array} [args.violations] - rule violations for the period
 * @returns {Object} normalized cost metrics
 */
export function computeRuleBreakCosts({ metrics = null, breaches = [], violations = [] } = {}) {
  const metricsData = metrics?.metrics || metrics || {};
  const precomputed = normalizePrecomputed(
    metricsData.rule_break_costs || metrics?.rule_break_costs || null
  );

  if (precomputed) {
    if (precomputed.total_impact === null) {
      precomputed.total_impact = sumImpact(precomputed);
    }
    return precomputed;
  }

  const no_stop_loss = computeNoStopCosts(violations);
  const daily_loss_unrecovered = computeDailyLossUnrecoveredCosts(breaches);
  const max_trades_excess = computeMaxTradesExcessCosts(violations);
  const parts = { no_stop_loss, daily_loss_unrecovered, max_trades_excess };

  return {
    ...parts,
    total_impact: sumImpact(parts),
    source: 'derived',
  };
}

export const RULE_BREAK_COST_EXPLANATIONS = {
  section:
    'Estimates how rule breaks showed up in P&L for this period. Figures use closed-trade results and finalized daily loss breaches — not theoretical risk.',
  no_stop_loss:
    'Sum and average realized P&L of trades that opened without a stop loss (excluding cases where a stop was added shortly after). Only trades with a known closed P&L are included.',
  daily_loss_unrecovered:
    'On days the daily loss limit was hit and the day did not recover above the limit by the close: how much further below the limit end-of-day P&L finished (excess beyond the limit). Pending (intraday) breaches are excluded until the day finalizes.',
  max_trades_excess:
    'On days max trades were exceeded: average (and total) realized P&L of trades placed after the last allowed trade that day. Shows whether overtrading helped or hurt.',
  total_impact:
    'Sum of no-stop-loss trade P&L + unrecovered daily-loss excess + P&L from trades after the max-trades limit. Positive means those breaks were net profitable in this window; negative means they cost you.',
};
